/* eslint-disable default-case */
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Card, Badge, Icon } from 'antd';
import Scrollbar from 'react-scrollbars-custom';
import 'moment/locale/pt-br';
import * as drawerSocietario from '~/containers/DrawerSocietario/actions';
import { LoadingSkeleton, LoadingSkeletonContainer } from './styles';
import ItemCard from './components/ItemCard';
import FinanceiroHeader from './components/FinanceiroHeader';
import FinanceiroStats from './components/FinanceiroStats';
import { filterInstallmentsByMonth, summarizeInstallments } from './components/ParcelasIndicator';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  FINANCEIRO_PAINEL_FILTERS_KEY,
  getDefaultFinanceiroPainelFilters,
  getFinanceiroPeriod,
} from './filters';

/**
 * Painel do Departamento Financeiro.
 *
 * Reaproveita exatamente os mesmos dados do painel Societário (state.dashTasks
 * alimentado por GET /dashtasks), porém com uma leitura financeira: cada card
 * mostra o progresso de pagamento das parcelas do serviço extra vinculado e
 * destaca tickets já concluídos que ainda estão em aberto.
 *
 * É uma visão de monitoramento (read-only): não há arrastar de cards entre
 * colunas, pois isso alteraria o status real da tarefa do Societário.
 */

const LISTS = {
  not_started: { name: 'Não Iniciado', style: { background: '#f5f5f5', color: '#1890ff' } },
  in_process: { name: 'Em Processo', style: { background: '#f5f5f5', color: '#faad14' } },
  done: { name: 'Concluido', style: { background: '#f5f5f5', color: '#52c41a' } },
};

const LIST_STYLE = { background: '#f5f5f5', padding: 8, width: '100%' };

const getMonthInstallments = (item, month) => {
  if (!item || !item.payment || !Array.isArray(item.payment.installments)) {
    return [];
  }
  return filterInstallmentsByMonth(item.payment.installments, month);
};

// Concluído mas ainda não quitado = parcela (no mês filtrado) ainda em aberto.
const hasOpenBalance = (item, month) => {
  return getMonthInstallments(item, month).some(function (p) {
    if (p.status === 'Pago') {
      return false;
    }
    return !(p.paid_at !== null && p.paid_at !== undefined && p.paid_at !== '');
  });
};

const Dashboard = (props) => {
  const { list, getCustomer, getList, showDrawer, user, isLoading } = props;

  // Filtros mantidos no refresh por alguns minutos (por escritório/usuário/página).
  // Tudo é filtrado no cliente: GET /dashtasks traz o quadro inteiro, então os
  // filtros restaurados valem direto, sem recarga.
  // KPIs sempre seguem um mês (atual por padrão). A lista, por padrão, abre na
  // visão de COBRANÇA: só não-quitados, de qualquer mês.
  const persisted = usePersistedFilters(FINANCEIRO_PAINEL_FILTERS_KEY, getDefaultFinanceiroPainelFilters);
  const filters = persisted.filters;
  const setFilters = persisted.setFilters;
  const screen = filters.screen;
  const search = filters.search || '';
  const paymentStatus = filters.paymentStatus;
  // Período personalizado { from, to } (YYYY-MM-DD). Quando existe, vale no lugar do mês.
  const period = getFinanceiroPeriod(filters);

  const handleMonthChange = function (value) {
    setFilters({ customRange: null, selectedMonth: value });
  };

  const handleCustomRange = function (from, to) {
    setFilters({ customRange: { from: from, to: to } });
  };

  // O mês escopa SEMPRE os KPIs (o resumo). Para a LISTA, "Em aberto"/"Vencidos"
  // são visões de cobrança e ignoram o mês (dívida de qualquer competência);
  // "Todos"/"Quitados" respeitam o mês selecionado.
  const monthApplies = paymentStatus === 'all' || paymentStatus === 'paid';
  const effectiveMonth = monthApplies ? period : null;

  // Calcula as 3 colunas (filtro de busca/status + ordenação) UMA vez por
  // mudança relevante. Reusado para a contagem e a renderização — evita
  // refazer o trabalho a cada tecla digitada na busca.
  const columns = useMemo(() => {
    const searchTerm = search.toLowerCase();

    const matchesSearch = (item) => {
      if (search === '') {
        return true;
      }
      const customer = getCustomer(item);
      const name = typeof customer.name === 'string' ? customer.name.toLowerCase() : '';
      const cod = typeof customer.cod === 'string' ? customer.cod.toLowerCase() : '';
      return name.includes(searchTerm) || cod.includes(searchTerm);
    };

    const matchesStatus = (item) => {
      const s = summarizeInstallments(getMonthInstallments(item, effectiveMonth), false);
      const has = !!s;
      const open = has && s.open > 0;
      const overdue = has && s.overdueCount > 0;
      if (paymentStatus === 'open') return open;
      if (paymentStatus === 'overdue') return overdue;
      if (paymentStatus === 'paid') return has && !open;
      if (!effectiveMonth) return true; // todos, sem mês -> tudo (inclui cortesia)
      return has; // todos, com mês -> tickets com parcela no mês
    };

    const built = {};
    Object.keys(LISTS).forEach((idList) => {
      const filtered = getList(idList).filter(function (item) {
        return matchesSearch(item) && matchesStatus(item);
      });
      filtered.sort(function (a, b) {
        if (idList === 'done') {
          const oa = hasOpenBalance(a, effectiveMonth) ? 1 : 0;
          const ob = hasOpenBalance(b, effectiveMonth) ? 1 : 0;
          if (oa !== ob) {
            return ob - oa;
          }
        }
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      built[idList] = filtered;
    });
    return built;
  }, [list, getList, getCustomer, search, paymentStatus, period, effectiveMonth]);

  return (
    <div>
      <FinanceiroHeader
        user={user}
        list={list}
        screen={screen}
        setScreen={function (value) { setFilters({ screen: value }); }}
        search={search}
        setSearch={function (value) { setFilters({ search: value }); }}
        selectedMonth={filters.selectedMonth}
        onMonthChange={handleMonthChange}
        customRange={filters.customRange}
        onCustomRange={handleCustomRange}
        paymentStatus={paymentStatus}
        setPaymentStatus={function (value) { setFilters({ paymentStatus: value }); }}
        monthApplies={monthApplies}
        persisted={persisted}
      />

      {screen === 'active' && (
        <FinanceiroStats list={list} month={period} isLoading={isLoading} />
      )}

      {screen === 'active' && (
        <Row gutter={16}>
          {Object.keys(LISTS).map((idList) => {
            const items = columns[idList] || [];
            return (
              <Col span={8} key={idList}>
                <Card
                  title={LISTS[idList].name}
                  bodyStyle={{ padding: 0 }}
                  headStyle={LISTS[idList].style}
                  extra={
                    <Badge
                      count={items.length}
                      showZero
                      style={{
                        backgroundColor: '#fff',
                        color: '#999',
                        boxShadow: '0 0 0 1px #d9d9d9 inset',
                      }}
                    />
                  }
                >
                  <div style={LIST_STYLE}>
                    <Scrollbar style={{ height: window.innerHeight - 240 }}>
                      {isLoading ? (
                        Array(5)
                          .fill(1)
                          .map((_, index) => (
                            <LoadingSkeletonContainer key={index}>
                              <LoadingSkeleton />
                            </LoadingSkeletonContainer>
                          ))
                      ) : items.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '40px 8px', fontSize: 13 }}>
                          <Icon type="inbox" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
                          Nenhum ticket nesta lente
                        </div>
                      ) : (
                        items.map((item) => (
                          <ItemCard
                            key={item.id}
                            item={item}
                            columnId={idList}
                            month={effectiveMonth}
                            getCustomer={getCustomer}
                            showDrawer={showDrawer}
                          />
                        ))
                      )}
                    </Scrollbar>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {screen === 'archived' && (
        <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '80px 20px', background: '#fff', borderRadius: 8, marginTop: 12 }}>
          <Icon type="file-done" style={{ fontSize: 40, display: 'block', marginBottom: 12, color: '#bfbfbf' }} />
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4, color: '#595959' }}>Tickets arquivados</div>
          <div style={{ fontSize: 13 }}>Use o painel Societário para consultar e gerenciar arquivados.</div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state.dashTasks,
  user: state.user,
  isLoading: state.dashTasks.isLoading,
  // Ordenação/filtragem (busca, mês, urgência) é feita no componente, onde o
  // mês selecionado está disponível. Aqui só devolvemos uma cópia da lista.
  getList: (list) => state.dashTasks[list].slice(),
  getCustomer: (item) => {
    if (item.deleted_customer) {
      return {
        name: item.deleted_customer.name,
      };
    }

    try {
      const customer = state.customersById[item.customer_id];
      if (customer) {
        return customer;
      }
    } catch (error) {
      console.log(error);
    }

    return {
      name: '-    -    -',
    };
  },
});

const mapDispatchToProps = (dispatch) => ({
  showDrawer: (category, data) => dispatch(drawerSocietario.show(category, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
